// src/pages/FriendChat.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../utils/axiosInstance';

const isMobile = () =>
  (typeof window !== 'undefined' &&
   window.matchMedia &&
   window.matchMedia('(max-width: 768px)').matches);

// bubble baseline (keeps your black-outline aesthetic)
const bubbleBase = {
  border: '2px solid #0e0e0e',
  borderRadius: 14,
  padding: '10px 12px',
  maxWidth: '78%',
  boxShadow: '0 2px 0 #0e0e0e',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
};

const FriendChat = () => {
  const { username } = useParams();
  const navigate = useNavigate();

  // measure: mobile = 100% content width, desktop = 2× nav tile width
  const measureWidth = () => {
    if (isMobile()) return '100%';
    const tile = document.querySelector('.nav-tabs .nav-item');
    if (!tile) return 520; // safe fallback
    const w = Math.round(tile.getBoundingClientRect().width) || 520;
    return Math.round(w * 2);
  };
  const [panelWidth, setPanelWidth] = useState(() => measureWidth());
  useEffect(() => {
    const onResize = () => setPanelWidth(measureWidth());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const myName = (() => { try { return localStorage.getItem('username') || ''; } catch { return ''; } })();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {_id, text, sender, senderName, createdAt, who}

  const listRef = useRef(null);
  const scrollToBottom = () => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  };
  useEffect(scrollToBottom, [messages]);

  // Normalize server rows -> add 'who'
  const normalize = (rows) =>
    (rows || []).map((r) => ({
      _id: String(r._id || `${r.createdAt}-${Math.random()}`),
      text: r.text,
      sender: String(r.sender || ''),
      senderName: r.senderName || '',
      createdAt: r.createdAt,
      who: (r.senderName && myName) ? (r.senderName.toLowerCase() === myName.toLowerCase() ? 'me' : 'them') : 'them',
    }));

  // Initial load
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const msgs = await axios.get(`/dm/${encodeURIComponent(username)}/messages`, { params: { limit: 100 } });
        if (!alive) return;
        const rows = msgs.data?.messages || msgs.data || [];
        setMessages(normalize(rows));
        // mark read best-effort
        try { await axios.post(`/dm/${encodeURIComponent(username)}/read`); } catch {}
      } catch (e) {
        console.error('Chat load failed:', e);
      }
    };
    run();
    return () => { alive = false; };
  }, [username]);

  // Poll every 3s
  useEffect(() => {
    let alive = true;
    const timer = setInterval(async () => {
      try {
        const res = await axios.get(`/dm/${encodeURIComponent(username)}/messages`, { params: { limit: 100 } });
        if (!alive) return;
        const rows = res.data?.messages || res.data || [];
        setMessages(normalize(rows));
      } catch {}
    }, 3000);
    return () => { alive = false; clearInterval(timer); };
  }, [username]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    const optimistic = {
      _id: `local-${Date.now()}`,
      text,
      senderName: myName,
      createdAt: new Date().toISOString(),
      who: 'me'
    };
    setMessages((m) => [...m, optimistic]);
    setInput('');
    try {
      await axios.post(`/dm/${encodeURIComponent(username)}/messages`, { text });
      // next poll will replace with server-truth
    } catch (e) {
      setMessages((m) => m.filter(x => x._id !== optimistic._id));
      alert('Message failed to send.');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const chatHeight = 420;

  return (
    <div className="scroll-shell">
      {/* scope a tiny override so Back gets real space even if global CSS uses !important */}
      <div className="scroll-body chat-page">
        <style>{`
          .chat-page .back-btn{ margin-bottom: 36px !important; }
        `}</style>

        {/* On mobile, remove the 980px cap so panel can truly be full-width */}
        <div style={{ maxWidth: isMobile() ? 'none' : 980, margin: '0 auto' }}>
          {/* Back */}
          <button
            className="logout-btn back-btn"
            onClick={() => navigate(-1)}
          >
            ← BACK
          </button>

          {/* Centered vertical chat panel */}
          <div
            style={{
              width: panelWidth,
              maxWidth: '100%',
              margin: '0 auto',
              background: '#fff',
              border: '2px solid #0e0e0e',
              borderRadius: 12,
              boxShadow: '0 2px 0 #0e0e0e',
              padding: 16
            }}
          >
            {/* Title */}
            <div
              style={{
                textAlign: 'center',
                fontWeight: 900,
                fontSize: 'clamp(1.4rem, 1rem + 1.4vw, 2rem)',
                lineHeight: 1.1
              }}
            >
              Chat with @{username}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#0e0e0e', margin: '10px 0 12px' }} />

            {/* Chat list */}
            <div
              ref={listRef}
              style={{
                border: '2px solid #0e0e0e',
                borderRadius: 12,
                background: '#fff',
                height: chatHeight,
                overflowY: 'auto',
                padding: 12,
                marginBottom: 12
              }}
            >
              {messages.length === 0 ? (
                <div style={{ color: '#666', textAlign: 'center', marginTop: 16 }}>
                  Start your conversation with @{username}!
                </div>
              ) : (
                messages.map((m) => (
                  <div
                    key={m._id}
                    style={{
                      display: 'flex',
                      justifyContent: m.who === 'me' ? 'flex-end' : 'flex-start',
                      margin: '12px 0'
                    }}
                  >
                    <div
                      style={{
                        ...bubbleBase,
                        background: m.who === 'me' ? '#0e0e0e' : '#fff',
                        color: m.who === 'me' ? '#fff' : '#111'
                      }}
                    >
                      {m.text}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Composer */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                alignItems: 'center'
              }}
            >
              <textarea
                className="input input-outline-black"
                rows={2}
                placeholder="Type a message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                style={{ resize: 'none', height: 48 }}
              />
              <button
                className="btn-outline-black"
                onClick={send}
                style={{ height: 48, padding: '0 16px' }}
              >
                SEND
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FriendChat;
